// ============================================================
// EMS CMDB — Recertification System Components
// Save as: src/RecertComponents.jsx
// Import into App.js
// ============================================================

import { useState, useEffect, useCallback } from "react";

// ============================================================
// RECERT STATUS UTILITIES
// ============================================================

export function getRecertStatusInfo(profile) {
  const status = profile?.recert_status;
  const dueDate = profile?.recert_due_date ? new Date(profile.recert_due_date) : null;
  const daysUntil = dueDate ? Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24)) : null;

  return {
    status,
    dueDate,
    daysUntil,
    isSuspended:  status === 'suspended_recert',
    isOverdue:    status === 'overdue',
    isDue:        status === 'due',
    isOk:         status === 'ok' || status == null,
    hasRequested: !!profile?.recert_requested_at,
  };
}

// ============================================================
// SUSPENDED / REAUTH REQUEST SCREEN
// Shown when user's recert is suspended — replaces entire app
// ============================================================

export function RecertSuspendedScreen({ profile, supabase, onSignOut }) {
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(!!profile?.recert_requested_at);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleRequest = async () => {
    setLoading(true);
    setError("");
    try {
      const { error: err } = await supabase.rpc("request_reauthorization", {
        request_note: note || null,
      });
      if (err) throw err;
      setSubmitted(true);
    } catch (e) {
      setError(e.message || "Request failed — please try again.");
    }
    setLoading(false);
  };

  const dueDate = profile?.recert_due_date
    ? new Date(profile.recert_due_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "Unknown";

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "radial-gradient(ellipse 80% 60% at 50% 30%, #1a0a0a 0%, #080b13 70%)",
      fontFamily: "var(--font)",
      padding: "2rem",
    }}>
      <div style={{
        background: "var(--bg2)",
        border: "1px solid rgba(239,68,68,0.3)",
        borderRadius: 16,
        padding: "2.5rem",
        width: 500,
        maxWidth: "100%",
      }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div style={{ fontSize: "3rem", marginBottom: "0.75rem" }}>🔒</div>
          <div style={{ fontSize: "1.5rem", fontWeight: 800, letterSpacing: "-1px", color: "var(--danger)", marginBottom: "0.5rem" }}>
            Access Suspended
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem", color: "var(--text2)", lineHeight: 1.7 }}>
            Your periodic access recertification was due on <strong style={{ color: "var(--text)" }}>{dueDate}</strong>.<br />
            Access has been automatically suspended pending reauthorization.
          </div>
        </div>

        {/* Info block */}
        <div style={{
          background: "rgba(239,68,68,0.07)",
          border: "1px solid rgba(239,68,68,0.2)",
          borderRadius: "var(--radius)",
          padding: "1rem",
          fontFamily: "var(--font-mono)",
          fontSize: "0.8rem",
          color: "var(--text2)",
          lineHeight: 1.7,
          marginBottom: "1.5rem",
        }}>
          {profile?.suspension_reason && (
            <div style={{ marginBottom: "0.5rem" }}>
              <strong style={{ color: "var(--text)" }}>Reason:</strong> {profile.suspension_reason}
            </div>
          )}
          <div>To regain access, submit a reauthorization request below. An administrator will review and restore your account.</div>
        </div>

        {submitted ? (
          /* Request submitted state */
          <div style={{
            background: "rgba(16,185,129,0.08)",
            border: "1px solid rgba(16,185,129,0.25)",
            borderRadius: "var(--radius)",
            padding: "1.25rem",
            textAlign: "center",
            fontFamily: "var(--font-mono)",
            fontSize: "0.85rem",
            color: "var(--success)",
            lineHeight: 1.7,
          }}>
            <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>✓</div>
            <strong>Reauthorization request submitted.</strong><br />
            <span style={{ color: "var(--text2)" }}>
              An administrator has been notified and will review your request.<br />
              You'll receive access once approved. Check back here to see if access has been restored.
            </span>
          </div>
        ) : (
          /* Request form */
          <>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontFamily: "var(--font-mono)", fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text2)", display: "block", marginBottom: "0.4rem" }}>
                Optional: Note to administrator
              </label>
              <textarea
                style={{
                  width: "100%",
                  background: "var(--bg)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  padding: "0.65rem 0.9rem",
                  color: "var(--text)",
                  fontFamily: "var(--font-mono)",
                  fontSize: "0.85rem",
                  outline: "none",
                  resize: "vertical",
                  minHeight: 80,
                }}
                placeholder="e.g. Still active — please reinstate access"
                value={note}
                onChange={e => setNote(e.target.value)}
              />
            </div>
            {error && (
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.8rem", color: "var(--danger)", marginBottom: "1rem", padding: "0.6rem 1rem", background: "rgba(239,68,68,0.1)", borderRadius: "var(--radius)" }}>
                {error}
              </div>
            )}
            <button
              onClick={handleRequest}
              disabled={loading}
              style={{
                width: "100%",
                padding: "0.85rem",
                background: "var(--accent)",
                border: "none",
                borderRadius: "var(--radius)",
                color: "white",
                fontFamily: "var(--font)",
                fontWeight: 700,
                fontSize: "1rem",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              {loading ? (
                <><span style={{ display: "inline-block", width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Submitting...</>
              ) : (
                "Submit Reauthorization Request"
              )}
            </button>
          </>
        )}

        <button
          onClick={onSignOut}
          style={{
            width: "100%",
            marginTop: "0.75rem",
            padding: "0.55rem",
            background: "transparent",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius)",
            color: "var(--text2)",
            fontFamily: "var(--font-mono)",
            fontSize: "0.8rem",
            cursor: "pointer",
          }}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}

// ============================================================
// RECERT WARNING BANNER
// Shown at top of app when recert is due soon
// ============================================================

export function RecertWarningBanner({ profile, supabase }) {
  const info = getRecertStatusInfo(profile);

  if (info.isOk || info.isSuspended) return null;

  const bgColor = info.isOverdue
    ? "rgba(239,68,68,0.1)"
    : "rgba(245,158,11,0.1)";
  const borderColor = info.isOverdue
    ? "rgba(239,68,68,0.3)"
    : "rgba(245,158,11,0.3)";
  const textColor = info.isOverdue ? "var(--danger)" : "var(--warning)";
  const icon = info.isOverdue ? "🔴" : "🟡";

  return (
    <div style={{
      background: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: 0,
      padding: "0.65rem 2rem",
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
      fontFamily: "var(--font-mono)",
      fontSize: "0.8rem",
      color: textColor,
      borderLeft: "none",
      borderRight: "none",
    }}>
      <span>{icon}</span>
      <span>
        {info.isOverdue
          ? `Your access recertification is overdue (was due ${info.dueDate?.toLocaleDateString()}). Access will be suspended soon if not completed.`
          : `Your access recertification is due in ${info.daysUntil} day${info.daysUntil !== 1 ? "s" : ""} (${info.dueDate?.toLocaleDateString()}). An administrator will recertify your account.`
        }
      </span>
      <span style={{ marginLeft: "auto", opacity: 0.7, fontSize: "0.72rem" }}>
        Contact your administrator if you believe this is an error
      </span>
    </div>
  );
}

// ============================================================
// ADMIN: RECERTIFICATION MANAGEMENT PAGE
// ============================================================

export function RecertManagementPage({ supabase, userEmail, toast }) {
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [actionLoading, setActionLoading] = useState({});
  const [editSettings, setEditSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [runningCron, setRunningCron] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [usersRes, settingsRes, historyRes] = await Promise.all([
      supabase.from("recert_dashboard").select("*"),
      supabase.from("recert_settings").select("*").eq("id", 1).single(),
      supabase.from("recert_history").select("*").order("created_at", { ascending: false }).limit(100),
    ]);
    setUsers(usersRes.data || []);
    setSettings(settingsRes.data);
    setSettingsForm(settingsRes.data || {});
    setHistory(historyRes.data || []);
    setLoading(false);
  }, [supabase]);

  useEffect(() => { load(); }, [load]);

  const pendingRequests = users.filter(u => u.recert_requested_at);
  const suspended      = users.filter(u => u.recert_status === "suspended_recert" && !u.recert_requested_at);
  const overdue        = users.filter(u => u.recert_status === "overdue");
  const dueSoon        = users.filter(u => u.recert_status === "due");
  const healthy        = users.filter(u => u.recert_status === "ok");

  const handleApprove = async (userId, note) => {
    setActionLoading(l => ({ ...l, [userId]: "approve" }));
    const { error } = await supabase.rpc("approve_recertification", {
      target_user_id: userId,
      admin_note: note || null,
    });
    if (error) toast("Approval failed: " + error.message, "error");
    else { toast("Access recertified successfully", "success"); load(); }
    setActionLoading(l => ({ ...l, [userId]: null }));
  };

  const handleDeny = async (userId) => {
    const reason = prompt("Reason for denial (optional):");
    if (reason === null) return; // cancelled
    setActionLoading(l => ({ ...l, [userId]: "deny" }));
    const { error } = await supabase.rpc("deny_recertification", {
      target_user_id: userId,
      deny_reason: reason || null,
    });
    if (error) toast("Denial failed: " + error.message, "error");
    else { toast("Request denied — user remains suspended", "info"); load(); }
    setActionLoading(l => ({ ...l, [userId]: null }));
  };

  const handleRunNow = async () => {
    setRunningCron(true);
    const { data, error } = await supabase.rpc("process_recertifications");
    if (error) toast("Run failed: " + error.message, "error");
    else {
      toast(`Check complete: ${data.warned} warned, ${data.overdue} overdue, ${data.suspended} suspended`, "success");
      load();
    }
    setRunningCron(false);
  };

  const handleSaveSettings = async () => {
    const { error } = await supabase
      .from("recert_settings")
      .update({
        default_interval:    settingsForm.default_interval,
        warning_days_before: parseInt(settingsForm.warning_days_before),
        grace_period_days:   parseInt(settingsForm.grace_period_days),
        auto_suspend:        settingsForm.auto_suspend,
        updated_at:          new Date().toISOString(),
      })
      .eq("id", 1);
    if (error) toast("Save failed: " + error.message, "error");
    else { toast("Settings saved", "success"); setEditSettings(false); load(); }
  };

  const UserCard = ({ user, showActions = true }) => {
    const daysLabel = user.days_until_due !== null
      ? user.days_until_due < 0
        ? `${Math.abs(user.days_until_due)}d overdue`
        : `due in ${user.days_until_due}d`
      : null;

    const statusColors = {
      ok:               { bg: "rgba(16,185,129,0.08)",  border: "rgba(16,185,129,0.2)",  text: "var(--success)" },
      due:              { bg: "rgba(245,158,11,0.08)",  border: "rgba(245,158,11,0.2)",  text: "var(--warning)" },
      overdue:          { bg: "rgba(239,68,68,0.08)",   border: "rgba(239,68,68,0.2)",   text: "var(--danger)"  },
      suspended_recert: { bg: "rgba(239,68,68,0.12)",   border: "rgba(239,68,68,0.35)",  text: "var(--danger)"  },
    };
    const colors = statusColors[user.recert_status] || statusColors.ok;

    return (
      <div style={{
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: "var(--radius)",
        padding: "1rem 1.25rem",
        marginBottom: "0.65rem",
        display: "flex",
        alignItems: "flex-start",
        gap: "1rem",
        flexWrap: "wrap",
      }}>
        {/* Avatar */}
        <div style={{
          width: 40, height: 40, borderRadius: "50%",
          background: "var(--bg3)",
          border: `1px solid ${colors.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 700, fontSize: "1rem", color: colors.text, flexShrink: 0,
        }}>
          {(user.full_name || user.email || "?")[0].toUpperCase()}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: "0.92rem", marginBottom: "0.2rem" }}>
            {user.full_name || "—"}
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem", color: "var(--text2)", marginBottom: "0.35rem" }}>
            {user.email}
            {" · "}
            <span style={{ color: colors.text }}>
              {user.recert_status === "suspended_recert" ? "⛔ Suspended" :
               user.recert_status === "overdue"          ? "🔴 Overdue" :
               user.recert_status === "due"              ? "🟡 Due Soon" : "✓ Current"}
            </span>
            {daysLabel && <span style={{ color: "var(--text2)" }}> · {daysLabel}</span>}
          </div>
          {user.recert_due_date && (
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.74rem", color: "var(--text2)" }}>
              Due: {new Date(user.recert_due_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              {user.last_recertified_at && (
                <> · Last certified: {new Date(user.last_recertified_at).toLocaleDateString()}</>
              )}
              {" · "}{user.recert_interval}
            </div>
          )}
          {user.recert_request_note && (
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem", color: "var(--text2)", marginTop: "0.4rem", padding: "0.4rem 0.75rem", background: "rgba(99,102,241,0.08)", borderRadius: 4, borderLeft: "2px solid var(--accent)" }}>
              User note: "{user.recert_request_note}"
            </div>
          )}
          {user.suspension_reason && !user.recert_request_note && (
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.74rem", color: "var(--danger)", marginTop: "0.35rem" }}>
              Reason: {user.suspension_reason}
            </div>
          )}
          {user.recert_requested_at && (
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.74rem", color: "var(--accent2)", marginTop: "0.35rem" }}>
              ⏳ Requested reauth: {new Date(user.recert_requested_at).toLocaleString()}
            </div>
          )}
        </div>

        {/* Actions */}
        {showActions && (
          <div style={{ display: "flex", gap: "0.4rem", flexShrink: 0, flexWrap: "wrap" }}>
            {(user.recert_status === "suspended_recert" || user.recert_requested_at) && (
              <>
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => handleApprove(user.id)}
                  disabled={actionLoading[user.id] === "approve"}
                >
                  {actionLoading[user.id] === "approve" ? "..." : "✓ Recertify"}
                </button>
                {user.recert_requested_at && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeny(user.id)}
                    disabled={actionLoading[user.id] === "deny"}
                  >
                    {actionLoading[user.id] === "deny" ? "..." : "✗ Deny"}
                  </button>
                )}
              </>
            )}
            {(user.recert_status === "ok" || user.recert_status === "due" || user.recert_status === "overdue") && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleApprove(user.id, "Manual recertification by administrator")}
                disabled={!!actionLoading[user.id]}
              >
                ↻ Recertify Now
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { id: "pending",   label: `⏳ Pending Requests (${pendingRequests.length})` },
    { id: "suspended", label: `⛔ Suspended (${suspended.length})` },
    { id: "overdue",   label: `🔴 Overdue (${overdue.length})` },
    { id: "due",       label: `🟡 Due Soon (${dueSoon.length})` },
    { id: "healthy",   label: `✓ Current (${healthy.length})` },
    { id: "history",   label: "📋 History" },
    { id: "settings",  label: "⚙ Settings" },
  ];

  const eventLabels = {
    RECERT_DUE:       { label: "Marked Due",       color: "badge-yellow" },
    RECERT_WARNING:   { label: "Warning Sent",      color: "badge-yellow" },
    RECERT_OVERDUE:   { label: "Overdue",           color: "badge-red"    },
    AUTO_SUSPENDED:   { label: "Auto-Suspended",    color: "badge-red"    },
    USER_REQUESTED:   { label: "User Requested",    color: "badge-blue"   },
    ADMIN_APPROVED:   { label: "Admin Approved",    color: "badge-green"  },
    ADMIN_DENIED:     { label: "Admin Denied",      color: "badge-red"    },
    MANUAL_SUSPENDED: { label: "Manually Suspended",color: "badge-red"    },
    MANUAL_REINSTATED:{ label: "Reinstated",        color: "badge-green"  },
    RECERT_COMPLETED: { label: "Certified",         color: "badge-green"  },
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">🔁 Recertification</div>
        <div className="page-actions">
          {settings?.last_run_at && (
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem", color: "var(--text2)" }}>
              Last check: {new Date(settings.last_run_at).toLocaleString()}
            </span>
          )}
          <button className="btn btn-secondary" onClick={handleRunNow} disabled={runningCron}>
            {runningCron ? "Running..." : "▶ Run Check Now"}
          </button>
        </div>
      </div>
      <div className="page-content">

        {/* Summary stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.85rem", marginBottom: "1.5rem" }}>
          {[
            { label: "Pending Requests", value: pendingRequests.length, color: "#6366f1" },
            { label: "Suspended",        value: suspended.length,       color: "#ef4444" },
            { label: "Overdue",          value: overdue.length,         color: "#ef4444" },
            { label: "Due Soon",         value: dueSoon.length,         color: "#f59e0b" },
            { label: "Current",          value: healthy.length,         color: "#10b981" },
          ].map((s, i) => (
            <div key={i} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderTop: `2px solid ${s.color}`, borderRadius: "var(--radius)", padding: "1rem" }}>
              <div style={{ fontSize: "1.8rem", fontWeight: 800, color: s.color, letterSpacing: "-1px" }}>{s.value}</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.68rem", color: "var(--text2)", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: "0.2rem" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tab bar */}
        <div className="tab-bar">
          {tabs.map(t => (
            <div key={t.id} className={`tab-item${tab === t.id ? " active" : ""}`} onClick={() => setTab(t.id)}>{t.label}</div>
          ))}
        </div>

        {/* PENDING REQUESTS */}
        {tab === "pending" && (
          <>
            {pendingRequests.length === 0
              ? <div style={{ textAlign: "center", padding: "3rem", color: "var(--text2)", fontFamily: "var(--font-mono)" }}>No pending reauthorization requests</div>
              : (
                <>
                  <div className="info-block accent" style={{ marginBottom: "1rem" }}>
                    These users have been suspended and have submitted a request to regain access. Review each request and approve or deny.
                  </div>
                  {pendingRequests.map(u => <UserCard key={u.id} user={u} />)}
                </>
              )}
          </>
        )}

        {/* SUSPENDED */}
        {tab === "suspended" && (
          <>
            {suspended.length === 0
              ? <div style={{ textAlign: "center", padding: "3rem", color: "var(--text2)", fontFamily: "var(--font-mono)" }}>No suspended users</div>
              : (
                <>
                  <div className="info-block danger" style={{ marginBottom: "1rem" }}>
                    These users are suspended and have not yet submitted a reauthorization request. You can proactively recertify or leave them suspended until they request access.
                  </div>
                  {suspended.map(u => <UserCard key={u.id} user={u} />)}
                </>
              )}
          </>
        )}

        {/* OVERDUE */}
        {tab === "overdue" && (
          <>
            {overdue.length === 0
              ? <div style={{ textAlign: "center", padding: "3rem", color: "var(--success)", fontFamily: "var(--font-mono)" }}>No overdue users</div>
              : overdue.map(u => <UserCard key={u.id} user={u} />)}
          </>
        )}

        {/* DUE SOON */}
        {tab === "due" && (
          <>
            {dueSoon.length === 0
              ? <div style={{ textAlign: "center", padding: "3rem", color: "var(--success)", fontFamily: "var(--font-mono)" }}>No users due soon</div>
              : dueSoon.map(u => <UserCard key={u.id} user={u} />)}
          </>
        )}

        {/* HEALTHY */}
        {tab === "healthy" && (
          healthy.map(u => <UserCard key={u.id} user={u} showActions={true} />)
        )}

        {/* HISTORY */}
        {tab === "history" && (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Timestamp</th><th>User</th><th>Event</th><th>By</th><th>Note</th></tr></thead>
              <tbody>
                {history.length === 0
                  ? <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--text2)" }}>No history yet</td></tr>
                  : history.map(h => {
                    const ev = eventLabels[h.event_type] || { label: h.event_type, color: "badge-gray" };
                    return (
                      <tr key={h.id}>
                        <td style={{ whiteSpace: "nowrap" }}>{new Date(h.created_at).toLocaleString()}</td>
                        <td style={{ maxWidth: 160 }}>{h.user_email}</td>
                        <td><span className={`badge ${ev.color}`}>{ev.label}</span></td>
                        <td style={{ maxWidth: 140, color: "var(--text2)" }}>{h.performed_by_email || "System"}</td>
                        <td style={{ maxWidth: 260, whiteSpace: "normal", color: "var(--text2)", fontSize: "0.76rem" }}>{h.note || "—"}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

        {/* SETTINGS */}
        {tab === "settings" && settings && (
          <div style={{ maxWidth: 560 }}>
            <div className="settings-section">
              <div className="settings-section-title">🔁 Recertification Policy</div>

              {!editSettings ? (
                <>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem", color: "var(--text2)", display: "grid", gap: "0.65rem", marginBottom: "1.25rem" }}>
                    {[
                      ["Default Interval",     settings.default_interval    ],
                      ["Warning Period",        `${settings.warning_days_before} days before due`],
                      ["Grace Period",          `${settings.grace_period_days} days after due date`],
                      ["Auto-Suspend",          settings.auto_suspend ? "Enabled" : "Disabled"],
                      ["Last Check",            settings.last_run_at ? new Date(settings.last_run_at).toLocaleString() : "Never"],
                    ].map(([k, v]) => (
                      <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "0.45rem 0", borderBottom: "1px solid var(--border)" }}>
                        <span>{k}</span>
                        <strong style={{ color: "var(--text)" }}>{v}</strong>
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-secondary" onClick={() => setEditSettings(true)}>Edit Policy</button>
                </>
              ) : (
                <>
                  <div className="form-grid" style={{ gridTemplateColumns: "1fr", gap: "1rem", marginBottom: "1.25rem" }}>
                    <div className="form-group">
                      <label className="form-label">Default Recertification Interval</label>
                      <select className="form-select" value={settingsForm.default_interval || "quarterly"} onChange={e => setSettingsForm(f => ({ ...f, default_interval: e.target.value }))}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly (Recommended)</option>
                        <option value="biannual">Every 6 Months</option>
                        <option value="annual">Annual</option>
                      </select>
                      <div className="input-help">Applied to all new recertifications. Existing schedules are not changed until next recertification.</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Warning Period (days before due)</label>
                      <input className="form-input" type="number" min={1} max={30} value={settingsForm.warning_days_before || 14} onChange={e => setSettingsForm(f => ({ ...f, warning_days_before: e.target.value }))} />
                      <div className="input-help">Users will see a warning banner this many days before their due date.</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Grace Period (days after due before auto-suspend)</label>
                      <input className="form-input" type="number" min={0} max={14} value={settingsForm.grace_period_days || 3} onChange={e => setSettingsForm(f => ({ ...f, grace_period_days: e.target.value }))} />
                      <div className="input-help">0 = suspend immediately on due date. Recommended: 3–5 days.</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Auto-Suspend When Overdue</label>
                      <select className="form-select" value={settingsForm.auto_suspend ? "true" : "false"} onChange={e => setSettingsForm(f => ({ ...f, auto_suspend: e.target.value === "true" }))}>
                        <option value="true">Enabled (Recommended)</option>
                        <option value="false">Disabled — warn only</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.65rem" }}>
                    <button className="btn btn-primary" onClick={handleSaveSettings}>Save Policy</button>
                    <button className="btn btn-secondary" onClick={() => { setEditSettings(false); setSettingsForm(settings); }}>Cancel</button>
                  </div>
                </>
              )}
            </div>

            <div className="settings-section">
              <div className="settings-section-title">ℹ️ How Recertification Works</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.78rem", color: "var(--text2)", lineHeight: 1.8 }}>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong style={{ color: "var(--text)" }}>1. Warning phase</strong> — {settings.warning_days_before} days before due date, the user sees a yellow banner in the app. Their access continues normally.
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong style={{ color: "var(--text)" }}>2. Overdue phase</strong> — After the due date, the banner turns red and the user is marked overdue. They still have access during the {settings.grace_period_days}-day grace period.
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong style={{ color: "var(--text)" }}>3. Auto-suspension</strong> — After the grace period, access is automatically suspended. The user sees a locked screen and can submit a reauthorization request.
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong style={{ color: "var(--text)" }}>4. Admin review</strong> — You see the request here under "Pending Requests." Review and approve (restores access with a new due date) or deny (user stays suspended).
                </div>
                <div>
                  <strong style={{ color: "var(--text)" }}>Scheduling</strong> — The check runs automatically via Supabase Edge Function (set up below), or you can click "Run Check Now" at any time.
                </div>
              </div>
            </div>

            <div className="settings-section">
              <div className="settings-section-title">⏰ Automated Scheduling</div>
              <div className="info-block accent" style={{ marginBottom: "1rem" }}>
                Supabase's pg_cron (free tier) runs the check automatically daily. To enable it, paste this into your SQL Editor:
              </div>
              <pre style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "1rem", fontFamily: "var(--font-mono)", fontSize: "0.78rem", color: "var(--accent2)", overflowX: "auto", lineHeight: 1.6 }}>
{`-- Run once in Supabase SQL Editor:
CREATE EXTENSION IF NOT EXISTS pg_cron;

SELECT cron.schedule(
  'ems-recert-check',
  '0 6 * * *',
  $$SELECT public.process_recertifications();$$
);`}
              </pre>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem", color: "var(--text2)", marginTop: "0.75rem" }}>
                This runs every day at 6 AM UTC. Until pg_cron is set up, use "Run Check Now" above to manually trigger the process.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
